import { useState } from 'react';
import type { Address } from 'viem';
import { useWriteContract, useSimulateContract, useReadContract } from 'wagmi';

import SmallArtworkCard from 'components/SmallArtworkCard';
import TransactionModal from 'components/TransactionModal';
import InputEth from 'components/base/InputEth';
import Modal from 'components/base/Modal';
import AmountBreakDown from 'components/transactions/generic/AmountBreakdown';
import useTransactionStore from 'state/stores/transactions';

import { useQueryEffects } from 'hooks/react-query';
import useModalVisibility from 'hooks/use-modal-visibility';
import { useExhibitionIdForNft } from 'hooks/web3/use-exhibition-id-for-nft';
import { useGetOffer } from 'hooks/web3/use-get-offer';
import useMarketBalances from 'hooks/web3/use-market-balances';
import { useNftSeller } from 'hooks/web3/use-nft-seller';
import useReferral from 'hooks/web3/use-referral';
import { NFTMarket } from 'lib/abis/NFTMarket';
import { getContractAddress } from 'lib/addresses';
import { ChainId } from 'lib/chains';
import { ZERO_ADDRESS } from 'lib/constants';
import { makeOfferSchema } from 'schemas/forms/price';
import { getTokenFilter } from 'utils/inputs';
import { findMarketByType } from 'utils/markets/markets';
import { extractErrorMessage } from 'utils/revert-reasons';
import { calculateTransactionValue } from 'utils/transactions';
import { formatEther, parseEther } from 'utils/units';

import { NftFilter } from 'types/Nft';
import { ModalOfType } from 'types/modal';

const MODAL_KEY = 'MAKE_OFFER';
const ACTION = 'make-offer';

type MakeOfferModalOptions = ModalOfType<typeof MODAL_KEY>;

export default function MakeOfferModal() {
  const modal = useModalVisibility(MODAL_KEY);

  return (
    <Modal.Root open={modal.open} onOpenChange={modal.onOpenChange}>
      <Modal.Portal>
        <Modal.BlurOverlay />
        <Modal.PositionOverlay>
          <Modal.UnmountListener onUnmount={modal.onUnmount} />
          {modal.config && <MakeOfferModalWindow {...modal.config} />}
        </Modal.PositionOverlay>
      </Modal.Portal>
    </Modal.Root>
  );
}

function MakeOfferModalWindow(props: MakeOfferModalOptions) {
  const [price, setPrice] = useState<string>('');

  const txStore = useTransactionStore();

  const { offerMarket, nftFilter } = getNormalizedValues(props);

  const marketBalancesQuery = useMarketBalances(nftFilter);

  const referralAddress = useReferral({
    contractAddress: nftFilter.contractAddress,
  });

  const getTransactionValue = (transactionValue: bigint) => {
    if (marketBalancesQuery.data) {
      return calculateTransactionValue({
        transactionValue,
        marketplaceBalance: marketBalancesQuery.data.availableFethBalance,
      });
    } else {
      return transactionValue;
    }
  };

  const makeOfferArgs = makeOfferSchema.safeParse({
    amount: price,
    referrer: referralAddress || ZERO_ADDRESS,
  });

  const getOfferQuery = useGetOffer(nftFilter);

  const getMinOfferAmountQuery = useReadContract({
    abi: NFTMarket,
    address: getContractAddress({
      chainId: nftFilter.chainId,
      contractName: 'nftMarket',
    }),
    chainId: nftFilter.chainId,
    functionName: 'getMinOfferAmount',
    args: [nftFilter.contractAddress, BigInt(nftFilter.tokenId)],
    query: {
      /**
       * if the NFT has an active offer, we set the
       * price to the next valdi active offer amount
       */
      enabled:
        getOfferQuery.isSuccess && getOfferQuery.data.buyer !== ZERO_ADDRESS,
    },
  });

  useQueryEffects(getMinOfferAmountQuery, {
    onSuccess: (amount) => setPrice(formatEther(amount)),
  });

  const simulateMakeOffer = useSimulateContract({
    abi: NFTMarket,
    functionName: 'makeOfferV2',
    address: getContractAddress({
      chainId: nftFilter.chainId,
      contractName: 'nftMarket',
    }),
    chainId: nftFilter.chainId,
    query: {
      enabled: makeOfferArgs.success,
      retry: false,
    },
    args: makeOfferArgs.success
      ? [
          nftFilter.contractAddress,
          BigInt(nftFilter.tokenId),
          parseEther(makeOfferArgs.data.amount),
          makeOfferArgs.data.referrer,
        ]
      : undefined,
    value: makeOfferArgs.success
      ? getTransactionValue(parseEther(makeOfferArgs.data.amount))
      : undefined,
  });

  const nftSellerQuery = useNftSeller(nftFilter, {
    enabled: makeOfferArgs.success,
  });

  const exhibitionByNft = useExhibitionIdForNft(
    {
      nftFilter,
      sellerPublicKey: nftSellerQuery.data || ZERO_ADDRESS,
    },
    { enabled: makeOfferArgs.success && nftSellerQuery.isSuccess }
  );

  const contractWrite = useWriteContract({
    mutation: {
      onSuccess: (txHash) => {
        txStore.startTracking({
          action: {
            name: ACTION,
            worldId: exhibitionByNft.data
              ? Number(exhibitionByNft.data.worldId)
              : null,
          },
          chainId: nftFilter.chainId,
          ui: 'toast',
          txHash,
          title: {
            PENDING: 'Making Offer…',
            SUCCESS: 'Offer made!',
          },
          description: {
            PENDING:
              'Your Offer is being confirmed on the blockchain. You can close this if you like.',
            SUCCESS:
              'Your Offer has been sent to the owner. They have 24 hours to accept your offer. If not accepted, the Offer will expire and will be returned to your Marketplace Balance.',
          },
        });
      },
    },
  });

  return (
    <TransactionModal
      chainId={nftFilter.chainId}
      preSignPrompt={{
        type: 'skip',
      }}
    >
      <TransactionModal.Content
        action={ACTION}
        chainId={nftFilter.chainId}
        txHash={contractWrite.data || null}
        footer={
          <Modal.Footer>
            <TransactionModal.TransactionButton
              chainId={nftFilter.chainId}
              isLoading={contractWrite.isPending || Boolean(contractWrite.data)}
              isDisabled={!simulateMakeOffer.isSuccess}
              write={() => {
                if (simulateMakeOffer.isSuccess) {
                  contractWrite.writeContract(simulateMakeOffer.data.request);
                }
              }}
              error={extractErrorMessage({
                simulateError: simulateMakeOffer.error,
                parsedZodSchema: makeOfferArgs,
              })}
              label="Make Offer"
            />
          </Modal.Footer>
        }
      >
        <TransactionModal.Body>
          <Modal.BodyTitle
            align="left"
            title="Make an Offer"
            description="Once an Offer is sent, the owner will have 24 hours to accept. If the Offer expires, the funds will be available in your Marketplace Balance."
          />
          {props.version === 1 ? (
            <SmallArtworkCard.Legacy
              activeOffer={offerMarket}
              artwork={props.artwork}
            />
          ) : (
            <SmallArtworkCard.MarketNft
              nft={props.nft}
              activeOffer={props.nft.activeOffer}
            />
          )}
          <TransactionModal.InputWrapper>
            <InputEth
              id="price"
              value={price}
              onChange={(event) => {
                setPrice(event.target.value);
              }}
            />
            <AmountBreakDown.Available />
          </TransactionModal.InputWrapper>
        </TransactionModal.Body>
      </TransactionModal.Content>
    </TransactionModal>
  );
}

const getNormalizedValues = (options: MakeOfferModalOptions) => {
  if (options.version === 1) {
    const { artwork } = options;

    const nftFilter: NftFilter = {
      chainId: artwork.chainId as ChainId,
      contractAddress: artwork.contractAddress as Address,
      tokenId: artwork.tokenId as number,
    };

    return {
      buyNowMarket: findMarketByType(artwork, 'BUY_NOW') || null,
      offerMarket: findMarketByType(artwork, 'OFFER') || null,
      nftFilter,
    };
  } else {
    const { nft } = options;

    return {
      nftFilter: getTokenFilter(nft),
      buyNowMarket: nft.activeBuyNow
        ? { amountInEth: nft.activeBuyNow.amount }
        : null,
      offerMarket: nft.activeOffer
        ? {
            amountInEth: nft.activeOffer.amount,
            eventDate: nft.activeOffer.expiryDate,
          }
        : null,
    };
  }
};
